import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import { t } from '../i18n';

function LangFlag({ code, active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={`flex items-center justify-center w-9 h-9 rounded-md border transition-all duration-200 ${
        active
          ? 'border-teal-700 bg-white shadow-sm'
          : 'border-teal-900/20 opacity-60 hover:opacity-100 hover:border-teal-600'
      }`}
    >
      <ReactCountryFlag
        countryCode={code}
        svg
        aria-hidden="true"
        style={{ width: '1.35em', height: '1.35em' }}
        title={label}
      />
    </button>
  );
}

export default function Header({ lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const c = t(lang);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#producto', label: c.nav.product },
    { href: '#funciones', label: c.nav.features },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0C3C3C]/95 backdrop-blur-md shadow-lg shadow-teal-950/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-[4.25rem]">
          <a href="/" className="flex items-baseline gap-1 select-none group">
            <span className="font-display text-xl md:text-2xl font-bold tracking-tight text-white">
              DigiSpace
            </span>
            <span className="font-display text-xl md:text-2xl font-bold tracking-tight text-teal-300 group-hover:text-teal-200 transition-colors">
              Prime
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-teal-100/85 hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <LangFlag
                code="MX"
                active={lang === 'es'}
                label="Español (México)"
                onClick={() => setLang('es')}
              />
              <LangFlag
                code="GB"
                active={lang === 'en'}
                label="English"
                onClick={() => setLang('en')}
              />
            </div>
            <a
              href="/s"
              className="px-4 py-2 rounded-md bg-teal-300 text-teal-950 font-semibold text-sm hover:bg-white transition-colors"
            >
              {c.nav.login}
            </a>
          </div>

          <button
            className="md:hidden p-2 rounded-md text-teal-100 hover:text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#0C3C3C] border-t border-teal-800/60 px-4 py-4 flex flex-col gap-3">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-teal-50 font-medium py-2"
            >
              {link.label}
            </a>
          ))}
          <div className="flex items-center gap-1.5 py-1">
            <LangFlag
              code="MX"
              active={lang === 'es'}
              label="Español"
              onClick={() => {
                setLang('es');
                setMobileOpen(false);
              }}
            />
            <LangFlag
              code="GB"
              active={lang === 'en'}
              label="English"
              onClick={() => {
                setLang('en');
                setMobileOpen(false);
              }}
            />
          </div>
          <a
            href="/s"
            className="px-4 py-2.5 rounded-md bg-teal-300 text-teal-950 font-semibold text-sm text-center"
          >
            {c.nav.login}
          </a>
        </div>
      )}
    </header>
  );
}
